import { TipoDespesaBackendDTO } from '../../models/gerenciar-contas/despesa.model';

/**
 * Simbolos de moeda para exibir valores monetarios do contrato do contador
 * conforme a moeda (objeto localizado do backend).
 */
const SIMBOLOS_MOEDA: Record<string, string> = {
  GUARANI: 'Gs',
  REAL: 'R$',
  DOLAR: '$',
  PESO: '$',
};

/**
 * Indica se o tipo de gasto e relacionado a IVA (recebe destaque visual).
 *
 * O backend serializa o enum TipoDespesa como objeto localizado
 * ({ enum, nome, ... }); aceita string por legado. Considera IVA quando o
 * nome do enum contem "IVA" (ex.: IVA_GENERAL, RETENCOES_DE_IVA).
 *
 * Convencao compartilhada entre o card do dashboard
 * (`DashboardContadorNotificacoesComponent`) e a pagina de detalhes
 * (`NotificacoesContadorComponent`) — mantida em um unico lugar para evitar
 * divergencia caso a regra de deteccao mude.
 *
 * @param tipo tipo de despesa (objeto localizado, string legado ou nulo)
 * @returns `true` quando o enum do tipo contem "IVA"; `false` caso contrario.
 */
export function ehIvaTipoDespesa(
  tipo: TipoDespesaBackendDTO | string | null | undefined,
): boolean {
  if (tipo == null) {
    return false;
  }
  const nomeEnum = typeof tipo === 'object' ? tipo.enum : tipo;
  return !!nomeEnum && nomeEnum.toUpperCase().includes('IVA');
}

/**
 * Formata um valor monetario conforme a moeda do contrato do contador.
 * GUARANI sem casas decimais; demais moedas com duas. Prefixa o simbolo da
 * moeda (Gs, R$, $) e retorna "-" quando o valor e nulo.
 *
 * @param valor     valor numerico (ex.: valor mensal do contrato)
 * @param moedaEnum codigo do enum de moeda (ex.: 'GUARANI', 'REAL'); assume
 *                  GUARANI quando ausente/indefinido.
 * @returns valor formatado com simbolo, ou "-" quando o valor e nulo.
 */
export function formatarValorMoedaContrato(
  valor: number | null,
  moedaEnum: string | null | undefined,
): string {
  if (valor == null) {
    return '-';
  }
  const codigo = moedaEnum || 'GUARANI';
  const simbolo = SIMBOLOS_MOEDA[codigo] ?? '';
  const casas = codigo === 'GUARANI' ? 0 : 2;
  const numero = valor.toLocaleString('es-PY', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
    useGrouping: true,
  });
  return `${simbolo} ${numero}`.trim();
}
